import React, { useState } from 'react';
import { X, ArrowRightLeft, Calculator, BookOpen, Save, Loader2 } from 'lucide-react';
import CustomerSearchInput from '../ui/CustomerSearchInput';
import ProductSearchInput from '../ui/ProductSearchInput';
import { filterBranchesForUser } from '../../utils/branchScope';

const QuotationForm = ({
  form, setForm, emptyQuote, editingQuoteId, setEditingQuoteId,
  customerSearch, setCustomerSearch, submitting,
  templates, showTemplates, setShowTemplates,
  showSaveTemplate, setShowSaveTemplate, templateName, setTemplateName,
  materialBreakdown, users, branches, salespersons = [], customers = [], products = [],
  customerShipAddresses = [],
  updateItem, addItem, removeItem,
  handleQuoteProductSelect, handleQuoteProductClear,
  updateGstRate, addOtherCharge, updateOtherCharge, removeOtherCharge,
  handleCreate, handleSaveTemplate, loadTemplate, deleteTemplate,
  onClose, onCustomerChange,
}) => {
  const productsList = Array.isArray(products) ? products : (products?.items || []);

  // Inline SKU search component
  const SkuSearchInput = ({ value, products: prods, onSelect, placeholder, testId }) => {
    const [search, setSearch] = useState(undefined);
    const [open, setOpen] = useState(false);
    const display = search !== undefined ? search : (value || '');
    const filtered = open && (search || '').length > 0
      ? prods.filter(p => p.sku?.toLowerCase().includes((search || '').toLowerCase())).slice(0, 8)
      : [];
    return (
      <div className="relative">
        <input
          data-testid={testId}
          value={display}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setSearch(value || ''); setOpen(true); }}
          onBlur={() => setTimeout(() => { setSearch(undefined); setOpen(false); }, 200)}
          placeholder={placeholder}
          className="w-full px-2 py-1.5 text-xs font-mono border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200"
        />
        {open && filtered.length > 0 && (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-40 overflow-y-auto">
            {filtered.map(p => (
              <div key={p.id} onMouseDown={() => { onSelect(p); setSearch(undefined); setOpen(false); }}
                className="px-2 py-1.5 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                <span className="font-mono font-semibold text-blue-700">{p.sku}</span>
                <span className="text-gray-400 ml-1.5">{p.name}</span>
              </div>
            ))}
          </div>
        )}
        {open && (search || '').length > 0 && filtered.length === 0 && (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="px-2 py-2 text-xs text-gray-400 text-center">No match</div>
          </div>
        )}
      </div>
    );
  };

  const freightOptions = [
    'FOR Destination (included in price)',
    'FOR Destination (extra at actual)',
    'Ex-Works (buyer to arrange pickup)',
    'Paid by Prismoline at actual',
    'To Pay (freight paid by buyer)',
  ];

  return (
    <div data-testid="smart-quote-builder">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Smart Quotation Builder</h2>
          <p className="text-[11px] text-gray-500">Auto unit conversion, margin analysis & material breakdown</p>
        </div>
        <button onClick={() => { onClose(); setForm({ ...emptyQuote }); }} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
      </div>

      {/* Template Actions */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setShowTemplates(!showTemplates)} data-testid="load-template-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 border border-blue-100">
          <BookOpen className="w-3 h-3" /> Load Template {templates.length > 0 && `(${templates.length})`}
        </button>
        {form.items.some(i => i.name) && (
          <button onClick={() => setShowSaveTemplate(true)} data-testid="save-template-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 border border-green-100">
            <Save className="w-3 h-3" /> Save as Template
          </button>
        )}
      </div>

      {/* Template List Dropdown */}
      {showTemplates && (
        <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 shadow-sm" data-testid="template-list">
          {templates.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">No templates saved yet. Create a quotation and save it as a template.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Saved Templates</p>
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50" data-testid={`template-${t.id}`}>
                  <div className="flex-1 cursor-pointer" onClick={() => loadTemplate(t)}>
                    <p className="text-xs font-semibold text-gray-900">{t.name}</p>
                    <p className="text-[10px] text-gray-400">{t.items?.length || 0} items | GST {t.gst_rate}% | by {t.created_by_name}</p>
                  </div>
                  <button onClick={() => deleteTemplate(t.id)} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500" data-testid={`delete-template-${t.id}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save Template Dialog */}
      {showSaveTemplate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="save-template-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'DM Sans' }}>Save as Template</h3>
            <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name (e.g. 'NHAI Standard Signage')"
              data-testid="template-name-input"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 mb-3" />
            <p className="text-[10px] text-gray-400 mb-3">Saves {form.items.filter(i => i.name).length} items, terms, and GST rate</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }} className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleSaveTemplate} disabled={!templateName.trim()} data-testid="confirm-save-template"
                className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Main form - 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Customer & Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Customer *</label>
                <CustomerSearchInput
                  value={form.customer_name || customerSearch || ''}
                  customerId={form.customer || ''}
                  onSelect={(c) => {
                    const spId = c.salesperson_id || '';
                    const sp = salespersons.find(s => s.id === spId);
                    setForm(prev => ({
                      ...prev, customer: c.id, customer_name: c.name,
                      customer_gstin: c.gstin || '', customer_address: c.address || '',
                      customer_city: c.city || '', customer_state: c.state || '',
                      customer_phone: c.phone || '',
                      ...(spId ? { salesman: spId, salesman_name: sp?.name || c.salesperson_name || '', salesman_phone: sp?.phone || '' } : {}),
                    }));
                    setCustomerSearch(c.name);
                    onCustomerChange?.(c.id);
                  }}
                  onClear={() => { setForm(prev => ({ ...prev, customer: '', customer_name: '', customer_gstin: '', customer_address: '', customer_city: '', customer_state: '', customer_phone: '' })); setCustomerSearch(''); onCustomerChange?.(null); }}
                  placeholder="Search customer..."
                  testId="quote-customer"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Salesman *</label>
                <select data-testid="quote-salesman" value={form.salesman} onChange={e => {
                  const sp = salespersons.find(x => x.id === e.target.value);
                  setForm(prev => ({ ...prev, salesman: e.target.value, salesman_name: sp?.name || '', salesman_phone: sp?.phone || '' }));
                }} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select Salesman</option>
                  {salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch</label>
                <select data-testid="quote-branch" value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select</option>
                  {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Valid Until *</label>
                <input data-testid="quote-valid-until" type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">GST Rate (%)</label>
                <select value={form.gst_rate} onChange={e => updateGstRate(e.target.value)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
            </div>
            {form.customer && (form.customer_gstin || form.customer_city || form.customer_phone) && (
              <div className="flex items-center gap-4 px-3 py-2 bg-blue-50/50 rounded-lg text-[10px] text-gray-600" data-testid="customer-details-display">
                {form.customer_gstin && <span className="font-mono">GSTIN: {form.customer_gstin}</span>}
                {(form.customer_address || form.customer_city) && <span>{[form.customer_address, form.customer_city, form.customer_state].filter(Boolean).join(', ')}</span>}
                {form.customer_phone && <span>Ph: {form.customer_phone}</span>}
              </div>
            )}
            {/* Ship To Details */}
            <div className="border border-dashed border-gray-200 rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Ship To (if different from billing)</label>
                {customerShipAddresses.length > 0 && (
                  <select data-testid="quote-ship-to-select"
                    onChange={e => {
                      if (!e.target.value) return;
                      const addr = customerShipAddresses.find(a => a.id === e.target.value || a.label === e.target.value);
                      if (addr) {
                        setForm(prev => ({ ...prev, ship_to_name: addr.name || addr.label || '', ship_to_address: addr.address || '', ship_to_city: addr.city || '', ship_to_state: addr.state || '' }));
                      }
                      e.target.value = '';
                    }}
                    className="text-[10px] px-2 py-1 border border-blue-200 rounded-md bg-blue-50 text-blue-700 cursor-pointer focus:outline-none"
                  >
                    <option value="">Select saved address</option>
                    {customerShipAddresses.map((a, i) => (
                      <option key={a.id || i} value={a.id || a.label}>{a.label || a.name || `Address ${i + 1}`} — {a.city || ''}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div>
                  <input data-testid="quote-ship-to-name" value={form.ship_to_name || ''} onChange={e => setForm({ ...form, ship_to_name: e.target.value })} placeholder="Consignee Name" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                </div>
                <div className="col-span-2">
                  <input data-testid="quote-ship-to-address" value={form.ship_to_address || ''} onChange={e => setForm({ ...form, ship_to_address: e.target.value })} placeholder="Address" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                </div>
                <div>
                  <input value={form.ship_to_city || ''} onChange={e => setForm({ ...form, ship_to_city: e.target.value })} placeholder="City" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                </div>
                <div>
                  <input value={form.ship_to_state || ''} onChange={e => setForm({ ...form, ship_to_state: e.target.value })} placeholder="State" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Project Name (optional)</label>
                <input data-testid="quote-project-name" value={form.project_name || ''} onChange={e => setForm({ ...form, project_name: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="e.g. NH-48 Signage Project" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Project Description (optional)</label>
                <input value={form.project_description || ''} onChange={e => setForm({ ...form, project_description: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Brief description" />
              </div>
            </div>
          </div>

          {/* Smart Line Items */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4" style={{ color: '#24549c' }} />
                <h3 className="text-xs font-bold text-gray-900">Line Items</h3>
                <span className="text-[10px] text-gray-400">Auto unit conversion enabled</span>
              </div>
              <button data-testid="add-quote-item" onClick={addItem} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add Item</button>
            </div>

            <div className="space-y-3">
              {form.items.map((item, idx) => (
                <div key={idx} data-testid={`quote-item-${idx}`} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50 relative">
                  {/* Floating delete button — always visible, not inside horizontal scroller */}
                  <button
                    onClick={() => removeItem(idx)}
                    data-testid={`quote-item-remove-${idx}`}
                    title="Remove this row"
                    aria-label="Remove line item"
                    className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-red-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 shadow-sm transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="overflow-x-auto pb-1" style={{ overflow: 'visible' }}>
                    <div className="grid gap-2 items-end" style={{ gridTemplateColumns: '110px 200px 80px 80px 70px 80px 70px 80px', minWidth: '780px' }}>
                      <div className="relative">
                        <label className="block text-[10px] text-gray-500 mb-0.5">SKU</label>
                        <SkuSearchInput
                          value={item.sku || ''}
                          products={productsList}
                          onSelect={(p) => handleQuoteProductSelect(idx, p)}
                          placeholder="Search SKU..."
                          testId={`quote-item-sku-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Product</label>
                        <ProductSearchInput value={item.name || ''} productId={item.product} onSelect={(p) => handleQuoteProductSelect(idx, p)} onChange={(text) => updateItem(idx, 'name', text)} onClear={() => handleQuoteProductClear(idx)} placeholder="Search product..." testId={`quote-item-product-${idx}`} />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">HSN</label>
                        <input value={item.hsn_code || ''} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} placeholder="HSN" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md font-mono" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Qty</label>
                        <input data-testid={`quote-item-qty-${idx}`} type="number" value={item.qty || ''} onChange={e => updateItem(idx, 'qty', e.target.value)} placeholder="0" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">UOM</label>
                        <input value={item.unit || ''} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50" readOnly={!!item.product} />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Rate</label>
                        <input data-testid={`quote-item-rate-${idx}`} type="number" value={item.rate || ''} onChange={e => updateItem(idx, 'rate', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Disc%</label>
                        <input type="number" value={item.discount || ''} onChange={e => updateItem(idx, 'discount', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                      </div>
                      <div className="text-right">
                        <label className="block text-[10px] text-gray-500 mb-0.5">Amount</label>
                        <p className="text-xs font-bold py-1.5" style={{ color: '#24549c' }}>₹{(item.amount || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  <input
                    value={item.description || ''}
                    onChange={e => updateItem(idx, 'description', e.target.value)}
                    placeholder="Item description / specifications..."
                    className="w-full mt-1.5 px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 bg-white"
                  />
                  {(item._converted_qty || item._margin > 0) && (
                    <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-gray-100">
                      {item._converted_qty && <div className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"><ArrowRightLeft className="w-3 h-3" /><span>{item.qty} {item.unit} {item._converted_qty}</span></div>}
                      {item._cost > 0 && <div className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">Cost: ₹{item._cost}/{item._buy_unit || item.unit}</div>}
                      {item._margin > 0 && <div className={`text-[10px] px-2 py-0.5 rounded-full ${item._margin >= 20 ? 'bg-green-50 text-green-700' : item._margin >= 10 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>Margin: {item._margin}%</div>}
                      {item._buy_unit && item._buy_unit !== item.unit && <div className="text-[10px] bg-orange-50 px-2 py-0.5 rounded-full text-orange-600">Buy: {item._buy_unit} | Sell: {item._sell_unit || item.unit}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Other Charges */}
            <div className="mt-4 border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase">Other Charges</h4>
                <button onClick={addOtherCharge} className="text-[10px] font-semibold px-2 py-0.5 rounded hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add Charge</button>
              </div>
              {(form.other_charges || []).map((charge, idx) => (
                <div key={idx} className="flex gap-2 mb-1.5 items-center">
                  <input value={charge.description} onChange={e => updateOtherCharge(idx, 'description', e.target.value)} placeholder="e.g. Freight, Handling, Packing" className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                  <input type="number" value={charge.amount || ''} onChange={e => updateOtherCharge(idx, 'amount', e.target.value)} placeholder="0" className="w-28 px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                  <button onClick={() => removeOtherCharge(idx)} className="p-1 hover:bg-red-50 rounded"><X className="w-3 h-3 text-red-400" /></button>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4">
              <div className="bg-gray-50 border border-gray-100 rounded-lg px-5 py-3 space-y-1 min-w-[240px]">
                <div className="flex justify-between text-xs"><span className="text-gray-500">Subtotal</span><span className="font-semibold">₹{form.subtotal.toLocaleString()}</span></div>
                {(form.other_charges || []).filter(c => c.amount > 0).map((c, i) => (
                  <div key={i} className="flex justify-between text-xs"><span className="text-gray-400">{c.description || 'Other'}</span><span>₹{Number(c.amount).toLocaleString()}</span></div>
                ))}
                <div className="flex justify-between text-xs"><span className="text-gray-500">GST ({form.gst_rate}%)</span><span>₹{(form.gst_amount || 0).toLocaleString()}</span></div>
                <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200" style={{ color: '#24549c' }}><span>Grand Total</span><span>₹{form.total.toLocaleString()}</span></div>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-3">Terms & Conditions</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(form.terms).map(([key, val]) => {
                if (key === 'freight') {
                  const isCustom = val && !freightOptions.includes(val);
                  return (
                    <div key={key}>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Freight / Delivery Terms</label>
                      <select data-testid="quote-freight-terms" value={isCustom ? '__custom__' : val} onChange={e => {
                        if (e.target.value === '__custom__') setForm({ ...form, terms: { ...form.terms, freight: '' } });
                        else setForm({ ...form, terms: { ...form.terms, freight: e.target.value } });
                      }} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                        {freightOptions.map(o => <option key={o} value={o}>{o}</option>)}
                        <option value="__custom__">Custom (type below)</option>
                      </select>
                      {isCustom && <input value={val} onChange={e => setForm({ ...form, terms: { ...form.terms, freight: e.target.value } })} placeholder="Enter custom freight terms" className="w-full mt-1 px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />}
                    </div>
                  );
                }
                return (
                  <div key={key}>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                    <input value={val} onChange={e => setForm({ ...form, terms: { ...form.terms, [key]: e.target.value } })} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => { onClose(); setForm({ ...emptyQuote }); setEditingQuoteId(null); setCustomerSearch(''); }} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button data-testid="save-draft-btn" onClick={() => handleCreate('Draft')} disabled={submitting} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}<Save className="w-3.5 h-3.5" />Save as Draft
            </button>
            <button data-testid="submit-quote-btn" onClick={() => handleCreate('Sent')} disabled={submitting} className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{editingQuoteId ? 'Update Quotation' : 'Create & Send'}
            </button>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 sticky top-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4" style={{ color: '#24549c' }} />
              <h3 className="text-xs font-bold text-gray-900">Material Requirement</h3>
            </div>
            {materialBreakdown.length === 0 ? (
              <p className="text-[11px] text-gray-400 py-4 text-center">Add items to see material breakdown</p>
            ) : (
              <div className="space-y-2">
                {materialBreakdown.map((m, i) => (
                  <div key={i} className="p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs font-semibold text-gray-900">{m.name}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                      <p className="text-[10px] text-gray-600">Sell: <strong>{m.sell_qty} {m.sell_unit}</strong></p>
                      {m.buy_qty && m.buy_unit !== m.sell_unit && <p className="text-[10px] text-orange-600">Buy: <strong>{m.buy_qty.toFixed(1)} {m.buy_unit}</strong></p>}
                      {m.conversions.map((c, j) => <p key={j} className="text-[10px] text-blue-600">= {c.qty} {c.unit}</p>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {form.items.some(i => i._cost > 0) && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Margin Analysis</h4>
                {form.items.filter(i => i.name).map((item, i) => {
                  const profit = item.amount - (item.qty * item._cost);
                  return (
                    <div key={i} className="flex justify-between text-[10px] py-1 border-b border-gray-50 last:border-0">
                      <span className="text-gray-600 truncate max-w-[120px]">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${item._margin >= 20 ? 'text-green-600' : item._margin >= 10 ? 'text-amber-600' : 'text-red-600'}`}>{item._margin}%</span>
                        <span className="text-gray-400">₹{profit.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between text-xs font-bold mt-2 pt-2 border-t border-gray-200">
                  <span>Total Profit</span>
                  <span style={{ color: '#24549c' }}>₹{form.items.reduce((s, i) => s + ((i.amount || 0) - (i.qty * (i._cost || 0))), 0).toLocaleString()}</span>
                </div>
              </div>
            )}
            {form.items.some(i => i._buy_unit && i._buy_unit !== i.unit) && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1.5"><ArrowRightLeft className="w-3.5 h-3.5 text-blue-600" /><p className="text-[10px] font-bold text-blue-700">Unit Conversion Active</p></div>
                <p className="text-[10px] text-blue-600">Some items have different buy/sell units. The material breakdown shows exact procurement quantities needed.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;
